<template>
	<slideover :open="show" title="Manage Document Links" @close="emit('close')">
		<div class="p-4 space-y-4">
			<div v-if="loadingLinks" class="text-center text-gray-500">Loading links...</div>
			<div v-else>
				<div v-if="links.length > 0" class="mb-6">
					<h4 class="font-bold mb-2">Existing Links</h4>
					<ul class="space-y-2">
						<li
							v-for="link in links"
							:key="link.id"
							class="bg-gray-50 p-3 rounded border flex justify-between items-center">
							<div>
								<div class="font-medium">
									{{ link.product_name || 'Product ' + link.product_id }}
								</div>
								<div class="text-sm text-gray-600">
									Page {{ link.page_number }}: {{ link.description }}
								</div>
							</div>
							<div class="flex space-x-2">
								<button
									class="text-blue-600 hover:text-blue-800"
									@click="editLink(link)">
									<app-icon :icon="['fas', 'edit']" />
								</button>
								<button
									class="text-red-600 hover:text-red-800"
									@click="removeLink(link)">
									<app-icon :icon="['fas', 'trash']" />
								</button>
							</div>
						</li>
					</ul>
				</div>

				<div class="border-t pt-4">
					<h4 class="font-bold mb-4">{{ editingLink ? 'Edit Link' : 'Add New Link' }}</h4>

					<app-select
						v-model="form.product_id"
						name="product_id"
						label="Product"
						:options="products"
						label-key="name"
						value-key="id"
						:error="errors.product_id"
						class="mb-4" />

					<app-input
						v-model="form.page_number"
						label="Page Number"
						type="number"
						:error="errors.page_number"
						class="mb-4" />

					<app-input
						v-model="form.description"
						label="Description"
						:error="errors.description"
						class="mb-4" />

					<div class="flex justify-end space-x-2">
						<app-button v-if="editingLink" theme="secondary" @click="cancelEdit">
							Cancel
						</app-button>
						<app-button :loading="submitting" theme="primary" @click="submit">
							{{ editingLink ? 'Update' : 'Add' }}
						</app-button>
					</div>
				</div>
			</div>
		</div>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, watch } from 'vue'
	import Slideover from '@/components/partials/Slideover.vue'
	import AppButton from '@/components/base/AppButton.vue'
	import AppInput from '@/components/base/forms/AppInput.vue'
	import AppSelect from '@/components/base/forms/AppSelect.vue'
	import AppIcon from '@/components/base/AppIcon.vue'
	import DocumentService from '@/services/document'
	import type { Document } from '@/data/schema/document'
	import type { DocumentLink } from '@/data/schema/document-link'

	const props = defineProps<{
		show: boolean
		document: Document
		products: any[]
	}>()

	const emit = defineEmits(['close'])

	const links = ref<DocumentLink[]>([])
	const loadingLinks = ref(false)
	const submitting = ref(false)
	const editingLink = ref<DocumentLink | null>(null)

	const form = ref<any>({
		product_id: '',
		page_number: '',
		description: ''
	})
	const errors = ref<any>({})

	const documentService = new DocumentService()

	const fetchLinks = async () => {
		if (!props.document) return
		loadingLinks.value = true
		try {
			const res = await documentService.getLinks(props.document.id)
			links.value = res.data
		} catch (e) {
			console.error('Error fetching links:', e)
		} finally {
			loadingLinks.value = false
		}
	}

	watch(
		() => props.show,
		(newVal) => {
			if (newVal) {
				fetchLinks()
				resetForm()
			}
		}
	)

	const resetForm = () => {
		form.value = {
			product_id: '',
			page_number: '',
			description: ''
		}
		editingLink.value = null
		errors.value = {}
	}

	const editLink = (link: DocumentLink) => {
		editingLink.value = link
		form.value = {
			product_id: link.product_id,
			page_number: link.page_number.toString(),
			description: link.description
		}
	}

	const cancelEdit = () => {
		resetForm()
	}

	const removeLink = async (link: DocumentLink) => {
		if (!confirm('Are you sure you want to remove this link?')) return
		try {
			await documentService.deleteLink(props.document.id, link.id)
			await fetchLinks()
		} catch (e) {
			console.error('Error deleting link:', e)
		}
	}

	const submit = async () => {
		errors.value = {}
		if (!form.value.product_id) errors.value.product_id = ['Product is required']
		if (!form.value.page_number) errors.value.page_number = ['Page number is required']
		if (!form.value.description) errors.value.description = ['Description is required']

		if (Object.keys(errors.value).length > 0) return

		submitting.value = true
		try {
			if (editingLink.value) {
				await documentService.updateLink(
					props.document.id,
					editingLink.value.id,
					form.value
				)
			} else {
				await documentService.addLink(props.document.id, form.value)
			}
			await fetchLinks()
			resetForm()
		} catch (e) {
			console.error('Error saving link:', e)
		} finally {
			submitting.value = false
		}
	}
</script>
